import { api, LightningElement } from "lwc";

import { CSRF_HEADER } from "@devforce/trailhead-client-api";
import { getEmitter } from "@devforce/trailhead-client-events";
import { getJSON } from "@devforce/trailhead-client-fetch";
import { logger } from "@devforce/trailhead-client-logger";

export const EVENT_LOGIN = "login";
export const EVENT_LOGOUT = "logout";

export const TRAILHEAD_EVENT_LOGIN = "trailhead_login";
export const TRAILHEAD_EVENT_LOGOUT = "trailhead_logout";
export const TRAILHEAD_EVENT_SIGNUP = "trailhead_signup";

export const TBID_EVENT_INIT = "tbid_will_init";
export const TBID_EVENT_LOGIN = "tbid_will_login";
export const TBID_EVENT_LOGOUT = "tbid_will_logout";

// fallbacks, remove after IIS
export const TBID_SCRIPT_PATH = "/resource/authProviderEmbeddedLogin_v1_3";
export const TBID_LOGOUT_PATH = "/services/auth/rp/oidc/logout";

export const TBID_INIT = "__tbid_init";
export const TBID_LOGIN = "__tbid_login";
export const TBID_LOGOUT = "__tbid_logout";

export const LOCK_KEY = "tbid.tabAuthLock";
export const LOCK_TIMEOUT = 5000;

declare var SFIDWidget: {
  init: () => void;
  login: () => void;
  logout: () => void;
  signup: () => void;
};

export default class extends LightningElement {
  @api authenticated: string | null = null;

  @api csrfToken: string = "";

  @api loginRedirectPath: string = null!;
  @api loginStatusPath: string = null!;
  @api loginTried: string | null = null;
  @api logoutRedirectPath: string = null!;

  @api tbidAuthProviderLoginPath: string = null!;
  @api tbidAuthProviderSignupPath: string = null!;
  @api tbidCommunityUri: string = null!;
  @api tbidRedirectUri: string = null!;
  @api embeddedLoginUri: string = null!;
  @api iframeLogoutUri: string = null!;

  private loadSFID: Promise<unknown> = null!;

  private get scriptSrc() {
    if (this.embeddedLoginUri) return `${this.embeddedLoginUri}`;

    return `${this.tbidCommunityUri}${TBID_SCRIPT_PATH}`;
  }

  private get logoutIframeSrc() {
    if (this.iframeLogoutUri) return `${this.iframeLogoutUri}`;

    return `${this.tbidCommunityUri}${TBID_LOGOUT_PATH}`;
  }

  connectedCallback() {
    this.appendMetaTag("salesforce-cache-max-age", "0");
    this.appendMetaTag("salesforce-brand", "trailhead");
    this.appendMetaTag("salesforce-community", this.tbidCommunityUri);
    this.appendMetaTag("salesforce-use-min-js", "false");

    this.appendMetaTag("salesforce-init-handler", TBID_INIT);
    this.appendMetaTag("salesforce-login-handler", TBID_LOGIN);
    this.appendMetaTag("salesforce-logout-handler", TBID_LOGOUT);

    this.appendMetaTag("salesforce-mode", "authprovider");
    this.appendMetaTag("salesforce-redirect-uri", this.tbidRedirectUri);

    this.appendMetaTag(
      "salesforce-authprovider-login",
      this.tbidAuthProviderLoginPath
    );
    this.appendMetaTag(
      "salesforce-authprovider-signup",
      this.tbidAuthProviderSignupPath
    );

    Object.defineProperty(window, TBID_INIT, {
      value: () => this.onInit(),
      writable: process.env.NODE_ENV === "test",
    });
    Object.defineProperty(window, TBID_LOGIN, {
      value: () => this.onLogin(),
      writable: process.env.NODE_ENV === "test",
    });
    Object.defineProperty(window, TBID_LOGOUT, {
      value: () => this.onLogout(),
      writable: process.env.NODE_ENV === "test",
    });

    this.loadSFID = loadSFID(this.scriptSrc).catch((error) => {
      this.dispatchEvent(
        new CustomEvent("error", {
          detail: { error },
        })
      );
    });

    let emitter = getEmitter();
    emitter.on("login", this.login);
    emitter.on("logout", this.logout);
    emitter.on("signup", this.signup);

    // Backward compatibility until trailhead-web
    // integrates @devforce/trailhead-client-events
    document.addEventListener(TRAILHEAD_EVENT_LOGIN, this);
    document.addEventListener(TRAILHEAD_EVENT_LOGOUT, this);
    document.addEventListener(TRAILHEAD_EVENT_SIGNUP, this);
  }

  disconnectedCallback() {
    let emitter = getEmitter();
    emitter.off("login", this.login);
    emitter.off("logout", this.logout);
    emitter.off("signup", this.signup);

    // Backward compatibility until trailhead-web
    // integrates @devforce/trailhead-client-events
    document.removeEventListener(TRAILHEAD_EVENT_LOGIN, this);
    document.removeEventListener(TRAILHEAD_EVENT_LOGOUT, this);
    document.removeEventListener(TRAILHEAD_EVENT_SIGNUP, this);
  }

  handleEvent(e: Event) {
    switch (e.type) {
      case TRAILHEAD_EVENT_LOGIN:
        this.login();
        break;
      case TRAILHEAD_EVENT_LOGOUT:
        this.logout();
        break;
      case TRAILHEAD_EVENT_SIGNUP:
        this.signup();
        break;
      default:
        break;
    }
  }

  private appendMetaTag(name: string, content: string) {
    let tag = document.createElement("meta");
    tag.name = name;
    tag.content = content;
    document.head.appendChild(tag);
  }

  private login = () => {
    SFIDWidget.login();
  };

  private logout = () => {
    SFIDWidget.logout();
  };

  private signup = () => {
    SFIDWidget.signup();
  };

  private onInit() {
    this.dispatchEvent(new CustomEvent(TBID_EVENT_INIT));
  }

  private async onLogin() {
    this.dispatchEvent(new CustomEvent(TBID_EVENT_LOGIN));
    this.dispatchEvent(new CustomEvent(EVENT_LOGIN));
    let lock = new Lock(LOCK_KEY, LOCK_TIMEOUT);
    let { user } = await getJSON<{ user: string }>(this.loginStatusPath, {
      headers: {
        [CSRF_HEADER]: this.csrfToken,
      },
    });
    let newSession =
      (user === "logged_in" && this.authenticated !== "true") ||
      (user === "not_logged_in" && this.loginTried !== "true");
    if (newSession) {
      if (lock.aquire()) window.location.href = this.loginRedirectPath;
      else
        setTimeout(() => {
          this.onLogin().catch((e: Error) => logger.warn(e.message));
        }, LOCK_TIMEOUT);
    } else {
      lock.release();
      this.onInit();
    }
  }

  private onLogout() {
    this.dispatchEvent(new CustomEvent(TBID_EVENT_LOGOUT));
    this.dispatchEvent(new CustomEvent(EVENT_LOGOUT));
    SFIDWidget.init();
    window.location.href = this.logoutRedirectPath;
  }
}

export class Lock {
  private key: string;
  private timeout: number;

  constructor(key: string, timeout: number) {
    this.key = key;
    this.timeout = timeout;
  }

  aquire() {
    if (this.locked) return false;
    sessionStorage.setItem(this.key, String(Date.now() + this.timeout));
    return true;
  }

  release() {
    sessionStorage.removeItem(this.key);
  }

  private get existingLock() {
    let storageValue = sessionStorage.getItem(this.key) || "";
    let value = parseInt(storageValue, 10);
    return Number.isNaN(value) ? 0 : value;
  }

  private get locked() {
    return this.existingLock > Date.now();
  }
}

function loadSFID(src: string) {
  let load = new Promise((resolve, reject) => {
    let script = document.createElement("script");
    script.onload = () => resolve(void 0);
    script.onerror = () => reject(new Error("TH_TBID_SFID_LOAD"));
    script.async = false;
    script.src = src;
    document.head.appendChild(script);
  });
  let timeout = new Promise((_, reject) =>
    setTimeout(reject, 5000, new Error("TH_TBID_SFID_TIMEOUT"))
  );
  return Promise.race([load, timeout]);
}
